import React, { useEffect } from "react";
import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(400px);
    opacity: 0;
  }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 100px;
  right: 20px;
  background: white;
  padding: 20px 25px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 15px;
  z-index: 10000;
  animation: ${slideIn} 0.3s ease forwards;
  max-width: 350px;

  &.closing {
    animation: ${slideOut} 0.3s ease forwards;
  }

  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    max-width: calc(100% - 20px);
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    switch (props.type) {
      case "success":
        return "#10b981";
      case "error":
        return "#ef4444";
      case "warning":
        return "#f59e0b";
      default:
        return "var(--primary-color)";
    }
  }};
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;

  h4 {
    margin: 0 0 5px 0;
    color: var(--text-dark);
    font-size: 1rem;
  }

  p {
    margin: 0;
    color: var(--text-light);
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-light);
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: var(--text-dark);
  }
`;

const Toast = ({
  message,
  title = "Success",
  type = "success",
  onClose,
  duration = 3000,
}) => {
  const [isClosing, setIsClosing] = React.useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case "success":
        return <i className="fas fa-check"></i>;
      case "error":
        return <i className="fas fa-times"></i>;
      case "warning":
        return <i className="fas fa-exclamation"></i>;
      default:
        return <i className="fas fa-info"></i>;
    }
  };

  return (
    <ToastContainer className={isClosing ? "closing" : ""}>
      <IconWrapper type={type}>{getIcon()}</IconWrapper>
      <Content>
        <h4>{title}</h4>
        <p>{message}</p>
      </Content>
      <CloseButton onClick={handleClose}>
        <i className="fas fa-times"></i>
      </CloseButton>
    </ToastContainer>
  );
};

export default Toast;
